import { useNavigate, useSearch } from '@tanstack/react-router'
import {
  DocumentHistoryItem,
  DocumentsSearchParams,
  DocumentTab,
  SearchHistoryItem,
  TabHistoryItem,
} from 'features/knowledgeDocumentViewer/types'
import { useCallback, useMemo, useState } from 'react'
import { DocumentViewerUrlPaths } from 'types'

type UpdateURLParams = Partial<DocumentsSearchParams>

export const createDefaultSearchHistoryItem = (): SearchHistoryItem => ({
  type: 'search',
  searchState: {
    searchMode: 'keyword',
    query: '',
    semanticQuery: '',
    filters: [],
    sorting: [],
    pagination: { pageIndex: 0, pageSize: 200 },
    selectedDocumentId: null,
  },
})

export const createDefaultTab = (id?: string): DocumentTab => ({
  id: id ?? `new-tab-${Date.now()}`,
  history: [createDefaultSearchHistoryItem()],
  currentIndex: 0,
})

export const createDocumentTab = (
  documentId: string,
  pageNumber: number = 1,
  selectedBoxIds: string[] = [],
  query: string = ''
): DocumentTab => {
  const docItem: DocumentHistoryItem = {
    type: 'document',
    documentId,
    pageNumber,
    selectedBoxIds,
    query,
  }
  return {
    id: `tab-${Date.now()}`,
    history: [docItem],
    currentIndex: 0,
  }
}

const createInitialTab = (
  documentID?: string,
  pageNumber?: number,
  selectedBoxes?: string[]
): DocumentTab => {
  if (documentID) {
    const docItem: DocumentHistoryItem = {
      type: 'document',
      documentId: documentID,
      pageNumber: pageNumber ?? 1,
      selectedBoxIds: selectedBoxes ?? [],
      query: '',
    }
    return {
      id: `tab-${Date.now()}`,
      history: [createDefaultSearchHistoryItem(), docItem],
      currentIndex: 1,
    }
  }
  return createDefaultTab()
}

export const useTabState = (basePath: DocumentViewerUrlPaths) => {
  const search = useSearch({ from: basePath })

  const [initialState] = useState(() => {
    const tab = createInitialTab(
      search.documentID,
      search.pageNumber,
      search.selectedBoxes
    )
    return { openTabs: [tab], activeTabId: tab.id }
  })

  const [openTabs, setOpenTabs] = useState<DocumentTab[]>(initialState.openTabs)
  const [activeTabId, setActiveTabId] = useState<string>(
    initialState.activeTabId
  )

  const activeTab = useMemo(() => {
    return openTabs.find((t) => t.id === activeTabId) ?? openTabs[0]
  }, [openTabs, activeTabId])

  const navigate = useNavigate({ from: basePath })

  const updateURLParams = useCallback(
    (updates: UpdateURLParams) => {
      navigate({
        search: (prev: DocumentsSearchParams) => ({
          ...prev,
          ...updates,
        }),
        replace: true,
      })
    },
    [navigate]
  )

  const writeHistoryItemToURL = useCallback(
    (item: TabHistoryItem) => {
      if (item.type === 'document') {
        updateURLParams({
          documentID: item.documentId,
          pageNumber: item.pageNumber,
          selectedBoxes: item.selectedBoxIds,
        })
      } else {
        updateURLParams({
          documentID: undefined,
          pageNumber: undefined,
          selectedBoxes: undefined,
        })
      }
    },
    [updateURLParams]
  )

  const addTab = useCallback(
    (tab: DocumentTab) => {
      const existingTab = openTabs.find((t) => t.id === tab.id)

      if (existingTab) {
        setActiveTabId(tab.id)
        writeHistoryItemToURL(existingTab.history[existingTab.currentIndex])
        return
      }

      setOpenTabs((prevTabs) => [...prevTabs, tab])
      setActiveTabId(tab.id)
      writeHistoryItemToURL(tab.history[tab.currentIndex])
    },
    [openTabs, writeHistoryItemToURL]
  )

  const addDocumentTab = useCallback(
    ({
      documentId,
      pageNumber = 1,
      selectedBoxIds = [],
    }: {
      documentId: string
      pageNumber?: number
      selectedBoxIds?: string[]
    }) => {
      addTab(createDocumentTab(documentId, pageNumber, selectedBoxIds))
    },
    [addTab]
  )

  const removeTab = useCallback(
    (tabId: string) => {
      if (openTabs.length === 1) {
        const freshTab = createDefaultTab()
        setOpenTabs([freshTab])
        setActiveTabId(freshTab.id)
        writeHistoryItemToURL(freshTab.history[0])
        return
      }

      const newTabs = openTabs.filter((t) => t.id !== tabId)
      setOpenTabs(newTabs)

      if (activeTabId === tabId && newTabs.length > 0) {
        const newActiveTab = newTabs[newTabs.length - 1]
        setActiveTabId(newActiveTab.id)
        writeHistoryItemToURL(newActiveTab.history[newActiveTab.currentIndex])
      }
    },
    [openTabs, activeTabId, writeHistoryItemToURL]
  )

  const pushToHistory = useCallback(
    (tabId: string, item: TabHistoryItem) => {
      setOpenTabs((prevTabs) => {
        return prevTabs.map((tab) => {
          if (tab.id !== tabId) return tab
          const newHistory = [
            ...tab.history.slice(0, tab.currentIndex + 1),
            item,
          ]
          return {
            ...tab,
            history: newHistory,
            currentIndex: newHistory.length - 1,
          }
        })
      })
      if (tabId === activeTabId) {
        writeHistoryItemToURL(item)
      }
    },
    [activeTabId, writeHistoryItemToURL]
  )

  const navigateHistory = useCallback(
    (tabId: string, direction: 'back' | 'forward') => {
      const tab = openTabs.find((t) => t.id === tabId)
      if (!tab) return

      const newIndex =
        direction === 'back' ? tab.currentIndex - 1 : tab.currentIndex + 1

      if (newIndex < 0 || newIndex >= tab.history.length) return

      const targetItem = tab.history[newIndex]

      setOpenTabs((prevTabs) =>
        prevTabs.map((t) =>
          t.id === tabId ? { ...t, currentIndex: newIndex } : t
        )
      )

      if (tabId === activeTabId) {
        writeHistoryItemToURL(targetItem)
      }
    },
    [openTabs, activeTabId, writeHistoryItemToURL]
  )

  const goBack = useCallback(
    (tabId: string) => navigateHistory(tabId, 'back'),
    [navigateHistory]
  )

  const goForward = useCallback(
    (tabId: string) => navigateHistory(tabId, 'forward'),
    [navigateHistory]
  )

  const updateDocumentHistoryItem = useCallback(
    (tabId: string, updates: Partial<Omit<DocumentHistoryItem, 'type'>>) => {
      setOpenTabs((prevTabs) => {
        return prevTabs.map((tab) => {
          if (tab.id !== tabId) return tab
          const currentItem = tab.history[tab.currentIndex]
          if (currentItem.type !== 'document') return tab
          const updatedItem: DocumentHistoryItem = {
            ...currentItem,
            ...updates,
          }
          const newHistory = [...tab.history]
          newHistory[tab.currentIndex] = updatedItem
          return { ...tab, history: newHistory }
        })
      })

      if (tabId === activeTabId && updates.selectedBoxIds !== undefined) {
        updateURLParams({ selectedBoxes: updates.selectedBoxIds })
      }
    },
    [activeTabId, updateURLParams]
  )

  const updateSearchHistoryItem = useCallback(
    (
      tabId: string,
      searchStateUpdates: Partial<SearchHistoryItem['searchState']>
    ) => {
      setOpenTabs((prevTabs) => {
        return prevTabs.map((tab) => {
          if (tab.id !== tabId) return tab
          const currentItem = tab.history[tab.currentIndex]
          if (currentItem.type !== 'search') return tab
          const updatedItem: SearchHistoryItem = {
            ...currentItem,
            searchState: { ...currentItem.searchState, ...searchStateUpdates },
          }
          const newHistory = [...tab.history]
          newHistory[tab.currentIndex] = updatedItem
          return { ...tab, history: newHistory }
        })
      })
    },
    []
  )

  const canGoBack = useMemo(() => {
    return activeTab.currentIndex > 0
  }, [activeTab])

  const canGoForward = useMemo(() => {
    return activeTab.currentIndex < activeTab.history.length - 1
  }, [activeTab])

  const activeHistoryItem = activeTab.history[activeTab.currentIndex]

  const switchToTab = useCallback(
    (tabId: string) => {
      const tab = openTabs.find((t) => t.id === tabId)
      if (tab) {
        setActiveTabId(tabId)
        writeHistoryItemToURL(tab.history[tab.currentIndex])
      }
    },
    [openTabs, writeHistoryItemToURL]
  )

  return {
    openTabs,
    activeTabId,
    setActiveTabId: switchToTab,
    addTab,
    addDocumentTab,
    removeTab,
    activeHistoryItem,
    pushToHistory,
    goBack,
    goForward,
    updateDocumentHistoryItem,
    updateSearchHistoryItem,
    canGoBack,
    canGoForward,
  }
}
